import React from 'react';
import { CostLineItem, COST_CATEGORY_LABELS, CostCategory } from '../../types/billing';
import { Card } from '../ui/Card';

interface CostSummaryCardProps {
  materials: CostLineItem[];
  labor: CostLineItem[];
  service: CostLineItem[];
  other: CostLineItem[];
  taxRate: number;
  currency?: string;
}

export const CostSummaryCard: React.FC<CostSummaryCardProps> = ({
  materials,
  labor,
  service,
  other,
  taxRate,
  currency = 'Rs',
}) => {
  const materialsSubtotal = materials.reduce((sum, i) => sum + i.amount, 0);
  const laborSubtotal = labor.reduce((sum, i) => sum + i.amount, 0);
  const serviceSubtotal = service.reduce((sum, i) => sum + i.amount, 0);
  const otherSubtotal = other.reduce((sum, i) => sum + i.amount, 0);
  const subtotal = materialsSubtotal + laborSubtotal + serviceSubtotal + otherSubtotal;
  const taxAmount = subtotal * (taxRate / 100);
  const total = subtotal + taxAmount;

  const rows: { label: string; value: number; highlight?: boolean }[] = [
    { label: COST_CATEGORY_LABELS.material, value: materialsSubtotal },
    { label: COST_CATEGORY_LABELS.labor, value: laborSubtotal },
    { label: COST_CATEGORY_LABELS.service, value: serviceSubtotal },
    { label: COST_CATEGORY_LABELS.other, value: otherSubtotal },
  ];

  return (
    <Card title="Cost Summary">
      <div className="space-y-2">
        {rows.map((row) => (
          <div key={row.label} className="flex justify-between text-xs">
            <span className="text-slate-400">{row.label}</span>
            <span className="text-slate-300 font-medium">{currency} {row.value.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
          </div>
        ))}

        <div className="border-t border-slate-700 my-2" />

        <div className="flex justify-between text-xs">
          <span className="text-slate-400">Subtotal</span>
          <span className="text-slate-200 font-semibold">{currency} {subtotal.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
        </div>
        <div className="flex justify-between text-xs">
          <span className="text-slate-400">Tax ({taxRate}%)</span>
          <span className="text-slate-300">{currency} {taxAmount.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
        </div>

        <div className="border-t border-slate-700 my-2" />

        <div className="flex justify-between">
          <span className="text-sm font-semibold text-slate-200">Total</span>
          <span className="text-lg font-semibold text-emerald-400">{currency} {total.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
        </div>
      </div>
    </Card>
  );
};
